import React, {useEffect, useState, useMemo} from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Typography,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tooltip,
    Divider,
    Container,
    Card,
    CardHeader,
    CardContent,
    Grid,
    IconButton,
    Badge
} from "@mui/material";
import {styled} from "@mui/material/styles";
import {DataGrid} from "@mui/x-data-grid";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import {LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import Slide from "@mui/material/Slide";
import LoadingButton from "@mui/lab/LoadingButton";
import SendIcon from "@mui/icons-material/Send";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import PhoneIcon from "@mui/icons-material/Phone";
import PaidIcon from "@mui/icons-material/Paid";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import CryptoJS from "crypto-js";
import {Field, Form, Formik} from "formik";
import {ApiDetails, KEY} from "../../Constants";
import { handle401Unauthorized, handle403Forbidden } from '../../utils/authErrorHandler';


// Transition animation for dialogs
const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

// Custom container with no horizontal padding
const NoPaddingContainer = styled(Container)(({theme}) => ({
    paddingLeft: 0,
    paddingRight: 0,
    maxWidth: '100%'
}));

// Styled components
const StyledDataGrid = styled(DataGrid)(({theme}) => ({
    "& .MuiDataGrid-root": {
        border: "none",
    },
    "& .MuiDataGrid-cell:focus": {
        outline: "none",
    },
    "& .MuiDataGrid-columnHeaders": {
        backgroundColor: "#4f6990",
        color: "white",
        fontSize: 14,
        fontWeight: 600,
        borderTopLeftRadius: 8,
        borderTopRightRadius: 8,
    },
    "& .MuiDataGrid-virtualScroller": {
        backgroundColor: theme.palette.background.paper,
    },
    "& .MuiDataGrid-footerContainer": {
        borderBottomLeftRadius: 8,
        borderBottomRightRadius: 8,
        borderTop: "1px solid #e0e0e0",
        backgroundColor: "#f9f9f9",
    },
    "& .MuiDataGrid-row:hover": {
        backgroundColor: "rgba(79, 105, 144, 0.08)",
    },
}));

const StyledCard = styled(Card)(({theme}) => ({
    borderRadius: 8,
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    overflow: "hidden",
    height: "100%",
}));

const StyledCardHeader = styled(CardHeader)(({theme}) => ({
    // backgroundColor: "#4f6990",
    background: `linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)`,
    color: "white",
    padding: theme.spacing(2),
    paddingLeft: 0,
    paddingRight: 0,
}));

const NoPaddingCardContent = styled(CardContent)(({theme}) => ({
    paddingLeft: 0,
    paddingRight: 0,
}));

const ActionButton = styled(Button)(({theme, color}) => ({
    borderRadius: 4,
    textTransform: "none",
    fontWeight: 500,
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    "&:hover": {
        boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
    },
}));

const ConfirmButton = styled(ActionButton)(({theme}) => ({
    backgroundColor: "#4caf50",
    color: "white",
    "&:hover": {
        backgroundColor: "#43a047",
    },
}));

export default function Transactions(props) {
    // Decrypt stored data
    let myMapDetailsDecrypted = CryptoJS.AES.decrypt(
        localStorage.getItem("myMap"),
        KEY
    );
    let portfolioDetailsDecrypted = CryptoJS.AES.decrypt(
        localStorage.portfolioDetails,
        KEY
    );
    let originalText = myMapDetailsDecrypted.toString(CryptoJS.enc.Utf8);
    let originalText2 = portfolioDetailsDecrypted.toString(CryptoJS.enc.Utf8);

    // State variables
    const [transactionDetailsModal, setOpen] = useState(false);
    const [confirmTransactionModal, setConfirmTransactionModal] = useState(false);
    const [details, setDetails] = useState({
        id: "",
        message: "",
        date: "",
        amount: "",
    });

    const [dateFrom, setDateFrom] = useState(new Date("1999-08-04"));
    const [dateTo, setDateTo] = useState(new Date());
    const [propertyName, setPropertyName] = useState(null);
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [transactions, setTransactions] = useState([]);
    const [searchResults, setSearchResults] = useState([]);
    const [selectedProperty, setSelectedProperty] = useState("");
    const [transactionLoading, setTransactionLoading] = useState(false);
    const [searchText, setSearchText] = useState("");

    // Loading screen component
    const loadingBackdrop = (
        <Backdrop
            sx={{color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1}}
            open={loading}
        >
            <CircularProgress color="inherit"/>
        </Backdrop>
    );

    // Fetch data on component mount
    useEffect(() => {
        fetchTransactions();
        fetchProperties();
        props.refreshSideBar();
    }, []);

    const fetchTransactions = () => {
        setLoading(true);
        fetch(ApiDetails + "pegasus/visionary/transactions/", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
            },
            body: JSON.stringify({
                landlord_id: JSON.parse(originalText)[0][1],
            }),
        })
            .then(async (response) => {
                let data = await response.json();
                // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
                if (response.status === 200) {
                    const transactionsArray = Array.isArray(data) ? data : data.transactions || [];
                    setTransactions(transactionsArray);
                    setSearchResults(transactionsArray);
                    setLoading(false);
                } else {
                    props.snackBar({
                        text: "Failed fetching transactions.",
                        type: "error",
                    });
                    setLoading(false);
                }
            })
            .catch((err) => {
                console.log(err);
                props.snackBar({
                    text: "Something Went Wrong. Call Customer Care For Assistance.",
                    type: "error",
                });
                setLoading(false);
            });
    };

    const fetchProperties = () => {
        fetch(ApiDetails + "pegasus/visionary/property/get/all/properties", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
            },
            body: JSON.stringify({
                landlord_id: JSON.parse(originalText)[0][1],
            }),
        })
            .then(async (response) => {
                let data = await response.json();
                // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
                if (response.status === 200) {
                    setProperties(data);
                } else {
                    props.snackBar({
                        text: "Failed fetching properties.",
                        type: "error",
                    });
                }
            })
            .catch((err) => {
                console.log(err);
                props.snackBar({
                    text: "Something Went Wrong. Call Customer Care For Assistance.",
                    type: "error",
                });
            });
    };

    // Dialog handlers
    const handleClose = () => {
        setOpen(false);
        setConfirmTransactionModal(false);
    };

    // Search functionality
    const searchTransactions = (value) => {
        if (!value) {
            setSearchResults(transactions);
            return;
        }

        setSearchResults(
            transactions.filter((item) =>
                (item.transaction_message || "")
                    .toLowerCase()
                    .includes(value.toLowerCase()) ||
                (item.transaction_id || "")
                    .toLowerCase()
                    .includes(value.toLowerCase()) ||
                (item.phone_number || "")
                    .toLowerCase()
                    .includes(value.toLowerCase())
            )
        );
    };

    const searchTransactionsByDate = () => {
        setSearchResults(
            transactions.filter((item) => {
                if (
                    new Date(item.date_created) >= dateFrom &&
                    new Date(item.date_created) <= dateTo
                ) {
                    return true;
                }
                return false;
            })
        );
    };

    // Data grid columns configuration
    const columns = [
        {
            field: "transaction_id",
            headerName: "Transaction ID",
            headerAlign: "center",
            align: "center",
            flex: 1,
            minWidth: 180,
            renderCell: (params) => (
                <Box sx={{display: 'flex', alignItems: 'center'}}>
                    <ReceiptIcon sx={{mr: 1, color: '#757575', fontSize: 18}}/>
                    <Typography variant="body2">{params.value}</Typography>
                </Box>
            ),
        },
        {
            field: "phone_number",
            headerName: "Phone No.",
            flex: 1,
            minWidth: 150,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => (
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                    <PhoneIcon sx={{mr: 1, color: '#757575', fontSize: 18}}/>
                    <Typography variant="body2">{params.value}</Typography>
                </Box>
            ),
        },
        {
            field: "amount",
            headerName: "Amount",
            flex: 1,
            minWidth: 150,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => (
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                    <PaidIcon sx={{mr: 1, color: params.row.amount > 0 ? '#2e7d32' : '#d32f2f', fontSize: 18}}/>
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: 'medium',
                            color: params.row.amount > 0 ? '#2e7d32' : '#d32f2f'
                        }}
                    >
                        {params.row.amount.toLocaleString("en-US", {
                            style: "currency",
                            currency: "KSH",
                        })}
                    </Typography>
                </Box>
            ),
        },
        {
            field: "payment_mode",
            headerName: "Payment Mode",
            flex: 1,
            minWidth: 150,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => {
                const gatewayType = params.row.gateway_type;
                const transactionId = params.row.transaction_id || "";
                // Determine if bank: use gateway_type if available, otherwise fall back to transaction_id pattern
                const isBank = gatewayType
                    ? gatewayType.toUpperCase() === "BANK"
                    : /^\*+/.test(transactionId);
                return (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: isBank ? 'rgba(25, 118, 210, 0.1)' : 'rgba(255, 143, 0, 0.1)',
                        py: 0.5,
                        px: 1.5,
                        borderRadius: 1,
                    }}>
                        {isBank ? (
                            <AccountBalanceIcon sx={{mr: 1, color: '#1976d2', fontSize: 18}}/>
                        ) : (
                            <SmartphoneIcon sx={{mr: 1, color: '#ff8f00', fontSize: 18}}/>
                        )}
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 'medium',
                                color: isBank ? '#1976d2' : '#ff8f00'
                            }}
                        >
                            {isBank ? 'Bank' : 'M-PESA'}
                        </Typography>
                    </Box>
                );
            }
        },
        {
            field: "date_created",
            headerName: "Date",
            flex: 1,
            minWidth: 180,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => (
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                    <CalendarTodayIcon sx={{mr: 1, color: '#757575', fontSize: 18}}/>
                    <Typography variant="body2">{params.value}</Typography>
                </Box>
            ),
        },
    ];

    // Form submission for confirming transactions
    const confirmTransaction = async (values) => {
        setTransactionLoading(true);

        const body = {
            transaction_code: values.transaction_code,
            property_id: selectedProperty
        };

        fetch(ApiDetails + "pegasus/visionary/mpesa/Confirm/Payment", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
            },
            body: JSON.stringify(body),
        })
            .then(async (response) => {
                let data = await response.json();
                // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
                if (response.status === 200) {
                    setTransactionLoading(false);
                    props.snackBar({
                        text: "Kindly wait for M-Pesa prompt on your phone",
                        type: "success",
                    });
                } else {
                    setTransactionLoading(false);
                    props.snackBar({
                        text: new Map(Object.entries(data)).get("Message") || "Transaction failed",
                        type: "error",
                    });
                }
                handleClose();
                // Refresh transactions after confirming payment
                fetchTransactions();
            })
            .catch((err) => {
                console.log(err);
                handleClose();
                setTransactionLoading(false);
                props.snackBar({
                    text: "Payment Not Successful",
                    type: "error",
                });
            });
    };

    const handlePropertyChange = (event) => {
        setSelectedProperty(event.target.value);
    };

    // Initial values for confirm transaction form
    const initialPaymentValues = {
        transaction_code: "",
    };

    return (
        <>
            {loading && loadingBackdrop}

            {/* Transaction Details Dialog */}
            <Dialog
                open={transactionDetailsModal}
                TransitionComponent={Transition}
                keepMounted
                aria-describedby="transaction-details-dialog"
                PaperProps={{
                    style: {
                        borderRadius: 8,
                        padding: 4,
                    },
                }}
            >
                <DialogTitle
                    sx={{borderBottom: 1, borderColor: 'divider', pb: 1.5, bgcolor: '#4f6990', color: 'white'}}>
                    <Box display="flex" alignItems="center">
                        <ReceiptIcon sx={{mr: 1}}/>
                        <Typography variant="h6" component="div">
                            Transaction Details
                        </Typography>
                    </Box>
                    <Typography variant="subtitle2" sx={{color: 'rgba(255, 255, 255, 0.8)', mt: 0.5}} gutterBottom>
                        ID: {details.id}
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{mt: 2}}>
                    <DialogContentText id="transaction-details-dialog-description">
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Typography variant="body2" paragraph>
                                    {details.message}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Box display="flex" alignItems="center">
                                    <PaidIcon sx={{mr: 1, color: '#2e7d32'}}/>
                                    <Typography variant="subtitle1" color="#2e7d32" fontWeight="medium">
                                        {details.amount ? details.amount.toLocaleString("en-US", {
                                            style: "currency",
                                            currency: "KSH",
                                        }) : ''}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <Box display="flex" alignItems="center">
                                    <CalendarTodayIcon sx={{mr: 1, color: '#757575', fontSize: 18}}/>
                                    <Typography variant="caption" color="text.secondary">
                                        {details.date}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleClose}
                        variant="contained"
                        size="small"
                        sx={{bgcolor: "#4f6990", "&:hover": {bgcolor: "#3d5278"}}}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirm Transaction Dialog */}
            <Dialog
                fullWidth
                maxWidth="sm"
                open={confirmTransactionModal}
                TransitionComponent={Transition}
                keepMounted
                aria-describedby="confirm-transaction-dialog"
                PaperProps={{
                    style: {
                        borderRadius: 8,
                    },
                }}
            >
                <DialogTitle sx={{bgcolor: '#4f6990', color: 'white'}}>
                    <Box display="flex" alignItems="center">
                        <SmartphoneIcon sx={{mr: 1}}/>
                        <Typography variant="h6" component="div">
                            Confirm M-Pesa Transaction
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{mt: 3}}>
                    <Formik
                        initialValues={initialPaymentValues}
                        onSubmit={confirmTransaction}
                    >
                        {(formikProps) => (
                            <Form noValidate>
                                <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                        <Field
                                            as={TextField}
                                            name="transaction_code"
                                            label="M-Pesa Transaction Code"
                                            required
                                            variant="outlined"
                                            fullWidth
                                            helperText="Enter the M-Pesa transaction code you received"
                                            InputProps={{
                                                startAdornment: (
                                                    <SmartphoneIcon sx={{mr: 1, color: '#ff8f00'}}/>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControl fullWidth variant="outlined">
                                            <InputLabel id="property-select-label">Property</InputLabel>
                                            <Select
                                                labelId="property-select-label"
                                                id="property-select"
                                                value={selectedProperty}
                                                label="Property"
                                                onChange={handlePropertyChange}
                                                startAdornment={<AccountBalanceIcon
                                                    sx={{mr: 1, ml: 1, color: '#4f6990'}}/>}
                                            >
                                                {properties.map((property) => (
                                                    <MenuItem
                                                        key={property.property_id}
                                                        value={property.property_id}
                                                    >
                                                        {property.property_name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>

                                <Box sx={{display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 2}}>
                                    <Button
                                        onClick={handleClose}
                                        variant="outlined"
                                        color="error"
                                        startIcon={<RefreshIcon/>}
                                    >
                                        Cancel
                                    </Button>
                                    <LoadingButton
                                        type="submit"
                                        variant="contained"
                                        sx={{
                                            bgcolor: "#4caf50",
                                            color: "white",
                                            "&:hover": {bgcolor: "#43a047"}
                                        }}
                                        loading={transactionLoading}
                                        startIcon={<SendIcon/>}
                                        loadingPosition="start"
                                    >
                                        Confirm Payment
                                    </LoadingButton>
                                </Box>
                            </Form>
                        )}
                    </Formik>
                </DialogContent>
            </Dialog>

            <NoPaddingContainer>
                <StyledCard>
                    <StyledCardHeader
                        title={
                            <Box display="flex" alignItems="center" sx={{pl: 2}}>
                                <Typography variant="h6" color="white">Transactions</Typography>
                            </Box>
                        }
                    />
                    <NoPaddingCardContent>
                        <Grid container spacing={3} sx={{mb: 3, mx: 0, width: '100%'}}>
                            <Grid item xs={12} md={7} sx={{pl: 0, pr: 0}}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                        border: 1,
                                        borderColor: '#e0e0e0',
                                        borderRadius: 2
                                    }}
                                >
                                    <Typography variant="body2" sx={{mr: 1, color: '#6c757d'}}>Date Range:</Typography>
                                    <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap'}}>
                                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                                            <DatePicker
                                                disableFuture
                                                label="From"
                                                value={dateFrom}
                                                onChange={(newValue) => {
                                                    setDateFrom(newValue);
                                                    if (dateTo) searchTransactionsByDate();
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        variant="outlined"
                                                        size="small"
                                                        {...params}
                                                    />
                                                )}
                                            />
                                        </LocalizationProvider>

                                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                                            <DatePicker
                                                disableFuture
                                                label="To"
                                                value={dateTo}
                                                onChange={(newValue) => {
                                                    setDateTo(newValue);
                                                    if (dateFrom) searchTransactionsByDate();
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        variant="outlined"
                                                        size="small"
                                                        {...params}
                                                    />
                                                )}
                                            />
                                        </LocalizationProvider>

                                        <IconButton
                                            onClick={searchTransactionsByDate}
                                            sx={{bgcolor: '#f8f9fa', color: '#6c757d', border: '1px solid #e0e0e0'}}
                                            size="small"
                                        >
                                            <SearchIcon fontSize="small"/>
                                        </IconButton>
                                    </Box>
                                </Paper>
                            </Grid>

                            <Grid item xs={12} md={5} sx={{pl: 0, pr: 0}}>
                                <Box sx={{display: 'flex', justifyContent: 'space-between', gap: 2}}>
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        label="Search Transactions"
                                        InputProps={{
                                            endAdornment: (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => searchTransactions(searchText)}
                                                >
                                                    <SearchIcon fontSize="small"/>
                                                </IconButton>
                                            ),
                                        }}
                                        value={searchText}
                                        onChange={(e) => {
                                            setSearchText(e.target.value);
                                            searchTransactions(e.target.value);
                                        }}
                                    />

                                    <ConfirmButton
                                        variant="outlined"
                                        startIcon={<ReceiptIcon/>}
                                        onClick={() => setConfirmTransactionModal(true)}
                                    >
                                        Confirm Transaction
                                    </ConfirmButton>
                                </Box>
                            </Grid>
                        </Grid>

                        <Box sx={{height: 500, width: '100%', px: 0}}>
                            <StyledDataGrid
                                rows={searchResults}
                                columns={columns}
                                pageSize={10}
                                rowsPerPageOptions={[10, 25, 50]}
                                disableSelectionOnClick
                                rowHeight={50}
                                getRowId={(row) => row.transaction_id}
                                loading={loading}
                                headerHeight={48}
                                components={{
                                    NoRowsOverlay: () => (
                                        <Box sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            height: '100%'
                                        }}>
                                            <Typography variant="body1" color="text.secondary">
                                                No Transactions Found
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Try adjusting your search or date filters
                                            </Typography>
                                        </Box>
                                    ),
                                }}
                                sx={{
                                    '& .MuiDataGrid-row:nth-of-type(even)': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                    },
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 1
                                }}
                                onRowClick={(params) => {
                                    setDetails({
                                        id: params.row.transaction_id,
                                        message: params.row.transaction_message || `${params.row.transaction_type || ""} - ${params.row.account_number || ""}`.trim() || "No details available",
                                        date: params.row.date_created,
                                        amount: params.row.amount,
                                    });
                                    setOpen(true);
                                }}
                            />
                        </Box>

                        <Box display="flex" justifyContent="flex-end" mt={2} pr={0}>
                            <Button
                                variant="outlined"
                                sx={{color: '#6c757d', borderColor: '#dee2e6'}}
                                startIcon={<RefreshIcon/>}
                                onClick={fetchTransactions}
                                size="small"
                            >
                                Refresh
                            </Button>
                        </Box>
                    </NoPaddingCardContent>
                </StyledCard>
            </NoPaddingContainer>
        </>
    );
}